import { IMAGES } from "@/lib/site";
import {
  Home as HomeIcon,
  Coffee,
  Bird,
  Camera,
  Sunrise,
  Users,
  Flower2,
  TreePine,
} from "lucide-react";

const features = [
  {
    icon: HomeIcon,
    title: "Traditional Malnad House",
    body: "Authentic architecture with warm wooden interiors, tiled roofs and open courtyards.",
  },
  {
    icon: TreePine,
    title: "Coffee Estate Surround",
    body: "Wake up to birdsong inside a working plantation of coffee, pepper and cardamom.",
  },
  {
    icon: Flower2,
    title: "Blooming Flower Garden",
    body: "A curated garden of seasonal flowers that welcomes you the moment you arrive.",
  },
  {
    icon: Users,
    title: "Family & Couple Friendly",
    body: "Spacious rooms, peaceful surroundings and warm hospitality for every guest.",
  },
];

const highlights = [
  { icon: Coffee, label: "Estate Walks" },
  { icon: Bird, label: "Bird Watching" },
  { icon: Camera, label: "Nature Photography" },
  { icon: Sunrise, label: "Peaceful Trails" },
];

const HomestayEstate = () => {
  return (
    <section
      id="estate"
      data-testid="estate-section"
      className="relative py-24 md:py-32 bg-[#1B4332] text-white overflow-hidden"
    >
      <span id="homestay" className="absolute -top-24" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] font-medium">
              Our Homestay & Coffee Estate
            </span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Rooted in Tradition,{" "}
            <span className="italic font-light text-[#E8C866]">
              Wrapped in Nature
            </span>
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed text-base md:text-lg max-w-2xl">
            Step into a working coffee estate where fresh mountain air,
            chirping birds and endless greenery create a truly magical
            atmosphere. From hand-crafted wooden pillars to aromatic filter
            coffee brewed at dawn — this is home, the Malnad way.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-center">
          {/* Owner-provided estate photos - stacked mosaic */}
          <div className="lg:col-span-7 reveal">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 sm:gap-4 h-[480px] md:h-[600px]">
              <div className="col-span-6 row-span-4 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={IMAGES.flowerCanopy}
                  alt="Flowering canopy walkway at Navilukallu"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="col-span-6 row-span-2 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={IMAGES.estateShrine}
                  alt="Beautifully lit garden shrine at night"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {highlights.map((h, i) => (
                <div
                  key={h.label}
                  className="flex items-center gap-3 py-3 px-4 rounded-xl border border-white/10 bg-white/5"
                  data-testid={`estate-highlight-${i}`}
                >
                  <span className="w-8 h-8 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]">
                    <h.icon size={14} strokeWidth={1.7} />
                  </span>
                  <span className="text-xs sm:text-sm text-white/85">
                    {h.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Feature cards */}
          <div className="lg:col-span-5 grid sm:grid-cols-2 gap-4">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="reveal p-6 rounded-2xl bg-white/5 backdrop-blur border border-white/10 hover:border-[#D4AF37]/50 hover:-translate-y-1 transition-all duration-300"
                style={{ animationDelay: `${i * 0.08}s` }}
                data-testid={`homestay-feature-${i}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]">
                  <f.icon size={20} strokeWidth={1.5} />
                </div>
                <div className="mt-4 font-heading text-lg">{f.title}</div>
                <div className="mt-2 text-white/65 text-sm leading-relaxed">
                  {f.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomestayEstate;
